import { X } from "lucide-react";
import { useMemo, useState } from "react";

import { DialogoCambiosSinGuardar } from "@/components/DialogoCambiosSinGuardar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { urlDeLaFotoDeLaFila, type CamposDeLaFila, type InstalacionDeInforme } from "@/lib/informes";
import { cn } from "@/lib/utils";

import {
  GrupoDeIdentificacionDeInstalacion,
  GrupoDeValorizacionDeInstalacion,
  TotalesEstimados,
} from "./CamposDeInstalacion";
import { RotuloDeGrupo } from "./CamposDeConstruccion";
import { mismosCampos } from "./DialogoDeEdicionDeFila";
import { FotosDeLaFila } from "./FotosDeLaFila";
import {
  camposDesdeInstalacion,
  formularioDesdeInstalacion,
  type FormularioDeInstalacion,
} from "./instalaciones";

interface DialogoDeInstalacionProps {
  /** La fila que se abre, o `null` con el modal cerrado. */
  fila: InstalacionDeInforme | null;
  informeId: number;
  ocupada: boolean;
  onCorregir: (filaId: number, campos: CamposDeLaFila) => void;
  onQuitar: (filaId: number) => void;
  onCerrar: () => void;
  onSubirFoto: (filaId: number, archivo: File) => void;
  onQuitarFoto: (filaId: number, fotoId: number) => void;
  onLeyendaDeFoto?: (filaId: number, fotoId: number, leyenda: string | null) => void;
}

/**
 * El modal "Editar instalación u obra": los campos, los dos totales estimados
 * y las fotos, con la validación y el "Quitar" al pie.
 *
 * SE REMONTA POR FILA (`key`): el estado de lo que se está escribiendo nace de
 * la fila que se abre y muere con el modal, así que abrir otra nunca hereda un
 * borrador ajeno.
 *
 * QUITAR VIVE ACÁ y no en la tabla, que es el diseño: la lista no tiene
 * selección en bloque. Quitar se lleva las fotos y no se deshace, así que el
 * botón pide un segundo clic que dice qué se va. Dice "Quitar instalación" y
 * no "Quitar" a secas: cada foto de abajo trae su propio "Quitar".
 */
export function DialogoDeInstalacion(props: DialogoDeInstalacionProps) {
  return props.fila === null ? null : <Contenido key={props.fila.id} {...props} fila={props.fila} />;
}

function textoDeQuitar(confirmando: boolean, fotos: number): string {
  if (!confirmando) return "Quitar instalación";
  if (fotos === 0) return "¿Quitar? Confirmar";

  return fotos === 1 ? "¿Quitar con su foto? Confirmar" : `¿Quitar con sus ${fotos} fotos? Confirmar`;
}

function Contenido({
  fila,
  informeId,
  ocupada,
  onCorregir,
  onQuitar,
  onCerrar,
  onSubirFoto,
  onQuitarFoto,
  onLeyendaDeFoto,
}: DialogoDeInstalacionProps & { fila: InstalacionDeInforme }) {
  const inicial = useMemo(
    () => formularioDesdeInstalacion(fila),
    // La fila llega de la caché y cambia de identidad con cada refetch (por
    // ejemplo, al subir una foto): lo que se escribe no se pisa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fila.id],
  );

  const [formulario, setFormulario] = useState<FormularioDeInstalacion>(inicial);
  const [casilla, setCasilla] = useState(fila.validada);
  const [casillaTocada, setCasillaTocada] = useState(false);
  const [rechazoDeFoto, setRechazoDeFoto] = useState<string | null>(null);
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);
  const [confirmandoQuitar, setConfirmandoQuitar] = useState(false);

  const cambio = !mismosCampos(formulario, inicial);
  const hayCambios = cambio || casillaTocada;

  /* LO VALIDADO NO SOBREVIVE A UN CAMBIO: si la fila estaba validada y se
     tocó un dato, vuelve a quedar sin validar — salvo que la persona haya
     vuelto a marcar la casilla en el modal, que es decir "ya lo revisé". */
  const vuelveASinValidar = fila.validada && cambio && !casillaTocada;
  const validadaAlGuardar = vuelveASinValidar ? false : casilla;

  const fotos = fila.fotos ?? [];

  function escribir(cambios: Partial<FormularioDeInstalacion>) {
    setFormulario((actual) => ({ ...actual, ...cambios }));
  }

  function pedirCierre() {
    if (hayCambios) setConfirmandoCierre(true);
    else onCerrar();
  }

  function guardar() {
    onCorregir(fila.id, { ...camposDesdeInstalacion(formulario), validada: validadaAlGuardar });
    onCerrar();
  }

  function quitar() {
    if (!confirmandoQuitar) {
      setConfirmandoQuitar(true);

      return;
    }

    onQuitar(fila.id);
    onCerrar();
  }

  const comunes = { idPrefijo: "instalacion-edicion", formulario, ocupada, escribir };

  return (
    <>
      <Dialog open onOpenChange={(abierto) => !abierto && pedirCierre()}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[calc(100vh-3.5rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-[55rem]"
        >
          <div className="border-divisor flex flex-none items-center gap-3 border-b-[1.5px] px-5 py-4">
            <div className="min-w-0 flex-1">
              <div className="text-ink-hint text-[0.65625rem] font-extrabold tracking-[0.07em] uppercase">
                Editar instalación u obra
              </div>
              <DialogTitle className="text-ink mt-1 truncate text-lg font-extrabold">
                {fila.tipo}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Los datos se guardan con «Guardar instalación». Las fotos se guardan apenas las
                sueltas.
              </DialogDescription>
            </div>

            {vuelveASinValidar ? (
              <span className="bg-warning-soft text-warning-soft-foreground rounded-md px-2.5 py-1 text-xs font-semibold">
                Al guardar cambios vuelve a quedar sin validar
              </span>
            ) : null}

            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Cerrar"
              title="Cerrar"
              onClick={pedirCierre}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          </div>

          <div className="flex min-h-0 flex-col gap-3.5 overflow-y-auto px-5 py-4.5">
            <RotuloDeGrupo>Identificación</RotuloDeGrupo>
            <GrupoDeIdentificacionDeInstalacion {...comunes} />

            {/* LA NOTA DE TERRENO ANTERIOR NO TIENE CAMPO EN EL DISEÑO, pero la
                fila que la trae la sigue mostrando: esconderla dejaría un dato
                guardado sin forma de verlo. Viaja de vuelta intacta al guardar. */}
            {fila.nota_terreno ? (
              <div className="border-divisor bg-superficie-sutil rounded-xl border-[1.5px] px-3.5 py-2.5">
                <div className="text-ink-muted text-[0.65625rem] font-extrabold tracking-[0.06em] uppercase">
                  Nota de terreno (anterior)
                </div>
                <p className="text-ink-body mt-1 text-xs font-semibold text-pretty">{fila.nota_terreno}</p>
              </div>
            ) : null}

            <div className="bg-divisor-suave h-px" />
            <RotuloDeGrupo>Valorización</RotuloDeGrupo>
            <GrupoDeValorizacionDeInstalacion {...comunes} />
            <TotalesEstimados formulario={formulario} />

            <div className="bg-divisor-suave h-px" />
            <FotosDeLaFila
              titulo="Fotos"
              etiquetaDelSelector="Adjuntar fotos de la instalación"
              ocupada={ocupada}
              editando
              pendientes={[]}
              guardadas={fotos}
              urlDe={(foto) =>
                urlDeLaFotoDeLaFila("instalaciones", informeId, fila.id, foto.id, foto.hash)
              }
              alt={`Foto de ${fila.tipo}`}
              error={rechazoDeFoto}
              // La fila YA existe: cada foto se va sola, sin esperar el botón de
              // abajo — las imágenes se guardan al soltarlas.
              onElegidas={(archivos) => archivos.forEach((a) => onSubirFoto(fila.id, a))}
              onRechazo={setRechazoDeFoto}
              onQuitarPendiente={() => {}}
              onQuitarGuardada={(fotoId) => onQuitarFoto(fila.id, fotoId)}
              onLeyenda={
                onLeyendaDeFoto
                  ? (fotoId, leyenda) => onLeyendaDeFoto(fila.id, fotoId, leyenda)
                  : undefined
              }
            />
          </div>

          <div className="border-divisor bg-card flex flex-none flex-wrap items-center gap-2.5 border-t-[1.5px] px-5 py-3">
            <label
              className={cn(
                "flex w-fit items-center gap-2 text-xs font-bold",
                casilla ? "text-success-soft-foreground" : "text-ink-body",
              )}
            >
              <Checkbox
                checked={casilla}
                disabled={ocupada}
                onCheckedChange={(v) => {
                  setCasilla(v === true);
                  setCasillaTocada(true);
                }}
              />
              Información validada
            </label>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={ocupada}
              title="Se quitan también sus fotos. No se puede deshacer."
              className="text-danger-soft-foreground hover:bg-danger-soft hover:text-danger-soft-foreground"
              onClick={quitar}
            >
              {textoDeQuitar(confirmandoQuitar, fotos.length)}
            </Button>

            <div className="ml-auto flex gap-2.5">
              <Button type="button" variant="outline" onClick={pedirCierre} disabled={ocupada}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={guardar}
                disabled={ocupada || formulario.tipo.trim() === ""}
              >
                {ocupada ? <Spinner /> : null}
                Guardar instalación
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DialogoCambiosSinGuardar
        abierto={confirmandoCierre}
        descripcion={
          "Vas a perder los cambios sin guardar en esta instalación. Las fotos que ya se " +
          "subieron no se deshacen: siguen en el informe aunque descartes el resto."
        }
        guardando={ocupada}
        onGuardarYSalir={() => {
          setConfirmandoCierre(false);
          guardar();
        }}
        onSalirSinGuardar={() => {
          setConfirmandoCierre(false);
          onCerrar();
        }}
        onCancelar={() => setConfirmandoCierre(false)}
      />
    </>
  );
}
